import { ncbAuthClient } from './ncbClient';

const USER_KEY = 'dm_current_user';
const USER_ID_KEY = 'user_id';

// 從 NCB 回傳格式中提取使用者
const extractUser = (response) => {
  return response?.user ||
         response?.data?.user ||
         response?.session?.user ||
         response?.data?.session?.user ||
         null;
};

const saveUser = (user) => {
  if (!user?.id) {
    throw new Error('Authentication succeeded without a user ID');
  }

  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(USER_ID_KEY, String(user.id));
  return user;
};

const clearUser = () => {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem('ncb_access_token');
};

/**
 * 🎈 [Nocodebackend 接入點：Authentication]
 * 完全對齊 NCB 官方指南的 API 邏輯
 */
export const authAdapter = {
  // 獲取啟用的登入方式 (對應指南中的 /providers)
  async getProviders() {
    try {
      const data = await ncbAuthClient('/providers', { method: 'GET' });
      return data.providers || { email: true };
    } catch (e) {
      console.warn('Failed to fetch providers, fallback to email only.', e);
      return { email: true };
    }
  },

  // 信箱登入 (對應指南中的 /sign-in/email)
  async signIn(email, password) {
    if (!email || !password) throw new Error('errors.missingCredentials');
    const response = await ncbAuthClient('/sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), password }),
    });

    const user = extractUser(response);
    return saveUser(user);
  },

  // 註冊帳號 (對應指南中的 /sign-up/email)
  async signUp(email, password) {
    if (!email || !password) throw new Error('errors.missingCredentials');
    const normalizedEmail = email.trim();
    const name = normalizedEmail.split('@')[0];
    const response = await ncbAuthClient('/sign-up/email', {
      method: 'POST',
      body: JSON.stringify({ name, email: normalizedEmail, password }), // 嚴格對齊指南的 payload
    });

    const user = extractUser(response);
    return saveUser(user);
  },

  // 登出 (對應指南中的 /sign-out)
  async signOut() {
    try {
      await ncbAuthClient('/sign-out', {
        method: 'POST',
        body: JSON.stringify({}), // 嚴格對齊指南要求送出空物件
      });
    } catch (e) {
      console.error('Sign out failed:', e);
    } finally {
      clearUser();
    }
  },

  // 驗證會話 (對應指南中的 /get-session)
  async getCurrentUser() {
    try {
      const response = await ncbAuthClient('/get-session', { method: 'GET' });
      const user = extractUser(response);
      
      if (user?.id) {
        saveUser(user);
        return user;
      }

      clearUser();
      return null;
    } catch (error) {
      clearUser();
      return null;
    }
  },

  // 請求重設密碼 (對應指南中的 /forget-password)
  async requestPasswordReset(email, redirectTo) {
    if (!email) throw new Error('errors.missingCredentials');
    const response = await ncbAuthClient('/forget-password', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), redirectTo }),
    });
    return response;
  },

  // 重設密碼 (對應指南中的 /reset-password)
  async resetPassword(newPassword, token) {
    if (!newPassword || !token) throw new Error('errors.missingCredentials');
    const response = await ncbAuthClient('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ newPassword, token }),
    });
    return response;
  },

  // 更改密碼 (對應指南中的 /change-password)
  async changePassword(currentPassword, newPassword, revokeOtherSessions = true) {
    if (!currentPassword || !newPassword) throw new Error('errors.missingCredentials');
    const response = await ncbAuthClient('/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword, revokeOtherSessions }),
    });
    return response;
  }
};